// Pure theory core for /chord-craft — coloring chords with extensions,
// voicing them across two hands, connecting them with voice leading, and
// looping them as progressions. Everything here is genre-agnostic: mood
// words and cross-genre "heard in" examples, never genre labels (those only
// show up as progression flavor text via `famousIn`). Builds a general
// chord-spec model (root + base triad + optional seventh/6th + stacked
// extensions) on top of the note/key tables in pianoDrills.js — no DOM, no
// audio, everything testable in plain Jest, same posture as the other music
// libs here (pianoDrills.js, circleOfFifths.js, chordCaller.js).

import { NOTE_NAMES_FLAT, NOTE_NAMES_SHARP, keyByName } from "./pianoDrills";

/* ---------- Chord spec model ---------- */
// { rootPc: 0-11, base: 'maj'|'min'|'dim'|'sus2'|'sus4',
//   seventh: null|'b7'|'maj7'|'6', extensions: string[] subset of
//   ['9','b9','#9','11','#11','13','b13'] }

export const BASES = [
  { id: "maj", label: "Major", intervals: [0, 4, 7] },
  { id: "min", label: "Minor", intervals: [0, 3, 7] },
  { id: "dim", label: "Diminished", intervals: [0, 3, 6] },
  { id: "sus2", label: "Sus2", intervals: [0, 2, 7] },
  { id: "sus4", label: "Sus4", intervals: [0, 5, 7] },
];

export const SEVENTHS = [
  { id: "b7", semis: 10 },
  { id: "maj7", semis: 11 },
  { id: "6", semis: 9 },
];

export const EXTENSIONS = [
  { id: "9", semis: 14 },
  { id: "b9", semis: 13 },
  { id: "#9", semis: 15 },
  { id: "11", semis: 17 },
  { id: "#11", semis: 18 },
  { id: "13", semis: 21 },
  { id: "b13", semis: 20 },
];

const baseById = (id) => BASES.find((b) => b.id === id) || null;
const seventhById = (id) => SEVENTHS.find((s) => s.id === id) || null;
const extensionById = (id) => EXTENSIONS.find((e) => e.id === id) || null;

const normPc = (n) => ((n % 12) + 12) % 12;

/* ---------- Tones & pitch classes ---------- */

// Semitone offsets from the root, ascending: base triad, then the
// seventh/6th (if any), then every extension present — "for voicing math",
// so duplicate pitch classes at different octave-ish offsets (a 6 at +9 and
// a 13 at +21 both land on the same pitch class) are kept distinct here;
// chordPcs is what dedupes.
export const chordTones = (spec) => {
  const info = baseById(spec.base);
  if (!info) return [];
  const offsets = [...info.intervals];
  const sev = spec.seventh ? seventhById(spec.seventh) : null;
  if (sev) offsets.push(sev.semis);
  (spec.extensions || []).forEach((id) => {
    const ext = extensionById(id);
    if (ext) offsets.push(ext.semis);
  });
  return offsets.sort((a, b) => a - b);
};

// Pitch classes 0-11 in stacking order root,3,5,(7),(exts), deduped.
export const chordPcs = (spec) => {
  const seen = new Set();
  const out = [];
  chordTones(spec).forEach((t) => {
    const pc = normPc(spec.rootPc + t);
    if (!seen.has(pc)) {
      seen.add(pc);
      out.push(pc);
    }
  });
  return out;
};

/* ---------- Chord symbol naming ---------- */

const NATURAL_ORDER = ["9", "11", "13"];
const ALTERED_ORDER = ["b9", "#9", "#11", "b13"];

// "C", "Cm", "Cmaj9", "C13b9", "F6/9", ... see the naming-rule table in the
// build spec: the highest *natural* extension in the chain 9->11->13 names
// the chord (falling back to the plain 7th, or nothing for a triad/add
// chord); altered extensions suffix on afterward in a fixed order.
export const chordSymbol = (spec, useFlats = false) => {
  const names = useFlats ? NOTE_NAMES_FLAT : NOTE_NAMES_SHARP;
  const root = names[normPc(spec.rootPc)];
  const extensions = spec.extensions || [];
  const naturalExts = NATURAL_ORDER.filter((e) => extensions.includes(e));
  const highestNatural = naturalExts.length ? naturalExts[naturalExts.length - 1] : null;
  const alteredSuffix = ALTERED_ORDER.filter((e) => extensions.includes(e)).join("");

  if (spec.base === "dim") {
    return `${root}°${alteredSuffix}`;
  }

  if (spec.base === "sus2" || spec.base === "sus4") {
    const sus = spec.base === "sus2" ? "sus2" : "sus4";
    if (!spec.seventh) {
      return `${root}${sus}${highestNatural ? `add${highestNatural}` : ""}${alteredSuffix}`;
    }
    const numberPart = highestNatural || "7";
    const sevLabel = spec.seventh === "6" ? "6" : spec.seventh === "maj7" ? `maj${numberPart}` : numberPart;
    return `${root}${sevLabel}${sus}${alteredSuffix}`;
  }

  const minor = spec.base === "min";
  const prefix = minor ? "m" : "";

  if (spec.seventh === "6") {
    let sym = `${root}${prefix}6`;
    if (extensions.includes("9")) sym += "/9";
    return sym + alteredSuffix;
  }

  if (!spec.seventh) {
    if (highestNatural) return `${root}${prefix}add${highestNatural}${alteredSuffix}`;
    return `${root}${prefix}${alteredSuffix}`;
  }

  const numberPart = highestNatural || "7";
  if (spec.seventh === "maj7") {
    return `${root}${prefix}maj${numberPart}${alteredSuffix}`;
  }
  // dominant / minor 7th family
  return `${root}${prefix}${numberPart}${alteredSuffix}`;
};

// Build a spec from a PALETTE preset id, rooted anywhere. Null for an
// unknown preset id.
export const specFromPreset = (presetId, rootPc) => {
  const preset = PALETTE.find((p) => p.id === presetId);
  if (!preset) return null;
  return {
    rootPc,
    base: preset.spec.base,
    seventh: preset.spec.seventh ?? null,
    extensions: [...(preset.spec.extensions || [])],
  };
};

/* ---------- Palette room data ---------- */
// Mood words + cross-genre "heard in" examples — never a genre label on the
// chord itself. Genre names only ever describe progressions (famousIn).

export const PALETTE = [
  {
    id: "maj7",
    label: "Maj7",
    spec: { base: "maj", seventh: "maj7", extensions: [] },
    mood: "lush, dreamy, floating",
    heardIn: "jazz ballads, bedroom pop, film-score love themes",
  },
  {
    id: "add9",
    label: "Add9",
    spec: { base: "maj", seventh: null, extensions: ["9"] },
    mood: "sparkly, open, unhurried",
    heardIn: "Coldplay pianos, The Cure arpeggios, film-score strings",
  },
  {
    id: "6",
    label: "6",
    spec: { base: "maj", seventh: "6", extensions: [] },
    mood: "warm, settled, a little old-fashioned",
    heardIn: "jazz standards, Beach Boys harmonies, lullabies",
  },
  {
    id: "6/9",
    label: "6/9",
    spec: { base: "maj", seventh: "6", extensions: ["9"] },
    mood: "the happy-ending chord — warm and fully at rest",
    heardIn: "Steely Dan endings, Disney final chords, and that mystery YouTube short's F6/9 ending",
  },
  {
    id: "sus4",
    label: "Sus4",
    spec: { base: "sus4", seventh: null, extensions: [] },
    mood: "suspended, anticipatory",
    heardIn: "worship music, Who power chords, ambient pads",
  },
  {
    id: "sus2",
    label: "Sus2",
    spec: { base: "sus2", seventh: null, extensions: [] },
    mood: "airy, unresolved",
    heardIn: "post-rock guitars, new-age piano, indie folk",
  },
  {
    id: "7",
    label: "7",
    spec: { base: "maj", seventh: "b7", extensions: [] },
    mood: "gritty, pulling forward",
    heardIn: "blues turnarounds, funk stabs, gospel cadences",
  },
  {
    id: "9",
    label: "9",
    spec: { base: "maj", seventh: "b7", extensions: ["9"] },
    mood: "funky, bright-edged",
    heardIn: "funk horn stabs, disco rhythm guitar, neo-soul keys",
  },
  {
    id: "13",
    label: "13",
    spec: { base: "maj", seventh: "b7", extensions: ["9", "13"] },
    mood: "big, brassy, wide open",
    heardIn: "big-band horn hits, gospel choir chords, city-pop grooves",
  },
  {
    id: "7b9",
    label: "7♭9",
    spec: { base: "maj", seventh: "b7", extensions: ["b9"] },
    mood: "tense, exotic pull",
    heardIn: "flamenco cadences, tango accordion, jazz turnarounds",
  },
  {
    id: "13b9",
    label: "13♭9",
    spec: { base: "maj", seventh: "b7", extensions: ["13", "b9"] },
    mood: "spicy, sophisticated",
    heardIn: "bebop endings, orchestral jazz, noir soundtracks",
  },
  {
    id: "m7",
    label: "m7",
    spec: { base: "min", seventh: "b7", extensions: [] },
    mood: "mellow, cool",
    heardIn: "lo-fi beats, bossa nova, R&B vamps",
  },
  {
    id: "m9",
    label: "m9",
    spec: { base: "min", seventh: "b7", extensions: ["9"] },
    mood: "silky, wistful",
    heardIn: "neo-soul progressions, city pop, ambient jazz",
  },
  {
    id: "m11",
    label: "m11",
    spec: { base: "min", seventh: "b7", extensions: ["9", "11"] },
    mood: "cloudy, spacious",
    heardIn: "neo-soul vamps, film-score tension cues, jam-band grooves",
  },
  {
    id: "madd9",
    label: "Madd9",
    spec: { base: "min", seventh: null, extensions: ["9"] },
    mood: "bittersweet, tender",
    heardIn: "indie ballads, singer-songwriter piano, string quartets",
  },
  {
    id: "m6",
    label: "m6",
    spec: { base: "min", seventh: "6", extensions: [] },
    mood: "noir, restless",
    heardIn: "spy-film scores, gypsy jazz, minor blues",
  },
  {
    id: "dim",
    label: "dim",
    spec: { base: "dim", seventh: null, extensions: [] },
    mood: "unsettled, sharp",
    heardIn: "horror stingers, classical passing chords, ragtime transitions",
  },
];

/* ---------- Voicing ---------- */

export const VOICING_LEVELS = [
  {
    id: 1,
    name: "Anchor",
    blurb: "Left hand holds the root alone; right hand plays the whole chord close together, near middle C.",
  },
  {
    id: 2,
    name: "Shells",
    blurb:
      "Left hand plays root + the defining tone (the 7th, or the 3rd when there's no 7th; the 6th for 6-chords); right hand fills in the rest, including any color tones.",
  },
  {
    id: 3,
    name: "Spread",
    blurb:
      "Left hand opens up to root + 5th (or a 10th when there's room); right hand stacks the 3rd/7th and color tones above.",
  },
  {
    id: 4,
    name: "Rootless",
    blurb:
      "Left hand plays a 4-note rootless shell (3-5-7-9 or 7-9-3-13) near middle C; right hand is free — add color or nothing at all, the root lives only in your ear.",
  },
];

const arrayDistance = (a, b) => {
  const n = Math.min(a.length, b.length);
  const sa = [...a].sort((x, y) => x - y);
  const sb = [...b].sort((x, y) => x - y);
  let sum = 0;
  for (let i = 0; i < n; i += 1) sum += Math.abs(sa[i] - sb[i]);
  return sum;
};

// Every octave-alignment of `rootPc` (stepping by 12) whose full offset set
// fits inside [low, high]. Empty when nothing fits cleanly — alignment to
// the right pitch class can cost up to 11 extra semitones beyond the raw
// offset span, so this has to actually search rather than compare spans.
const rootsInRange = (rootPc, offsets, low, high) => {
  if (!offsets.length) return [low + normPc(rootPc - low)];
  const minOffset = Math.min(...offsets);
  const maxOffset = Math.max(...offsets);
  const lowBound = low - minOffset;
  const highBound = high - maxOffset;
  if (highBound < lowBound) return [];
  const start = lowBound + normPc(rootPc - lowBound);
  const out = [];
  for (let R = start; R <= highBound; R += 12) out.push(R);
  return out;
};

// rootsInRange, with a single clamped (possibly overflowing) fallback when
// nothing fits cleanly, so placement never returns nothing.
const candidateRoots = (rootPc, offsets, low, high) => {
  const fits = rootsInRange(rootPc, offsets, low, high);
  return fits.length ? fits : [low + normPc(rootPc - low)];
};

// Every inversion (rotation) of a sorted offset stack: each step lifts the
// current lowest note an octave, keeping the same pitch classes with a
// different chord tone on the bottom.
const rotationsOf = (offsets) => {
  const sorted = [...offsets].sort((a, b) => a - b);
  const out = [sorted];
  let cur = sorted;
  for (let i = 1; i < sorted.length; i += 1) {
    cur = [...cur.slice(1), cur[0] + 12].sort((a, b) => a - b);
    out.push(cur);
  }
  return out;
};

// Place a set of root-relative offsets inside [low, high]: without a prev
// voicing, keep root position and pick the octave whose center sits nearest
// `target` (so lesson displays show the textbook stack); with a prev hand
// voicing, search every inversion at every octave and pick whichever moves
// least from it — inversions are what makes voice leading actually smooth
// (C-E-G to B-D-G, not a whole-hand leap to G-B-D). Ties prefer the earlier
// shape (closer to root position), then the lower placement.
const placeOffsets = (rootPc, offsets, low, high, target, prevMidis, allowInversions = true) => {
  if (!offsets.length) return [];
  const leading = prevMidis && prevMidis.length;
  const shapes =
    leading && allowInversions
      ? rotationsOf(offsets)
      : [[...offsets].sort((a, b) => a - b)];
  let best = null;
  shapes.forEach((shape) => {
    candidateRoots(rootPc, shape, low, high).forEach((R) => {
      const midis = shape.map((o) => R + o);
      const score = leading
        ? arrayDistance(prevMidis, midis)
        : Math.abs(midis.reduce((a, b) => a + b, 0) / midis.length - target);
      if (!best || score < best.score || (score === best.score && R < best.R)) {
        best = { R, midis, score };
      }
    });
  });
  return best.midis;
};

// { left: [midis], right: [midis] } for the chord at a voicing level.
// opts: { prev: {left,right}|null, lhLow, lhHigh, rhLow, rhHigh }.
export const voiceChord = (spec, level, opts = {}) => {
  const { prev = null, lhLow = 40, lhHigh = 59, rhLow = 57, rhHigh = 84 } = opts;
  const info = baseById(spec.base) || BASES[0];
  const thirdOffset = info.intervals[1];
  const fifthOffset = info.intervals[2];
  const sev = spec.seventh ? seventhById(spec.seventh) : null;
  const sevOffset = sev ? sev.semis : null;
  const extOffsets = (spec.extensions || [])
    .map((id) => extensionById(id))
    .filter(Boolean)
    .map((e) => e.semis)
    .sort((a, b) => a - b);
  const lhTarget = Math.max((lhLow + lhHigh) / 2, 48);
  // Clamp toward middle C rather than the raw range center: with the default
  // rhLow/rhHigh the center sits near C5, which strands close voicings an
  // octave above where a "near middle C" right hand should live.
  const rhTarget = Math.min(Math.max((rhLow + rhHigh) / 2, 60), 66);
  const prevLeft = prev && prev.left;
  const prevRight = prev && prev.right;

  if (level === 1) {
    return {
      left: placeOffsets(spec.rootPc, [0], lhLow, lhHigh, lhTarget, prevLeft),
      right: placeOffsets(spec.rootPc, chordTones(spec), rhLow, rhHigh, rhTarget, prevRight),
    };
  }

  if (level === 2) {
    const leftOffsets = sevOffset != null ? [0, sevOffset] : [0, thirdOffset];
    const leftSet = new Set(leftOffsets);
    const rightOffsets = chordTones(spec).filter((o) => !leftSet.has(o));
    return {
      // Shells keep the root on the bottom — no inversions for the left hand.
      left: placeOffsets(spec.rootPc, leftOffsets, lhLow, lhHigh, lhTarget, prevLeft, false),
      right: placeOffsets(spec.rootPc, rightOffsets, rhLow, rhHigh, rhTarget, prevRight),
    };
  }

  if (level === 3) {
    const tenthOffsets = [0, thirdOffset + 12];
    const fifthOffsets = [0, fifthOffset];
    const tenthFits = rootsInRange(spec.rootPc, tenthOffsets, lhLow, lhHigh).length > 0;
    const leftOffsets = tenthFits ? tenthOffsets : fifthOffsets;
    const rightOffsets = [thirdOffset, ...(sevOffset != null ? [sevOffset] : []), ...extOffsets].sort(
      (a, b) => a - b
    );
    return {
      // Root-and-fifth (or tenth) anchors likewise stay root-on-bottom.
      left: placeOffsets(spec.rootPc, leftOffsets, lhLow, lhHigh, lhTarget, prevLeft, false),
      right: placeOffsets(spec.rootPc, rightOffsets, rhLow, rhHigh, rhTarget, prevRight),
    };
  }

  // level 4: rootless shell, 3-5-7-9 or 7-9-3-13, whichever sits nearer
  // its zone (or moves less, when voice-leading off a previous chord).
  // Rootless voicings *imply* a seventh and ninth when the spec doesn't
  // carry them — that's the nature of the grip — but the implied seventh
  // follows the chord's quality (maj7 on plain major, b7 on minor) instead
  // of a blanket b7, and a 6-chord keeps its 6th (the classic 3-5-6-9).
  const nineOffset = extOffsets.find((o) => [13, 14, 15].includes(o)) ?? 14;
  const thirteenOffset = extOffsets.find((o) => [20, 21].includes(o)) ?? 21;
  const effSevOffset = sevOffset != null ? sevOffset : spec.base === "maj" ? 11 : 10;
  const shapeA = [thirdOffset, fifthOffset, effSevOffset, nineOffset].sort((a, b) => a - b);
  const shapeB = [effSevOffset, nineOffset, thirdOffset + 12, thirteenOffset].sort((a, b) => a - b);
  // The rootless zone sits just below and around middle C (topping out near
  // E4), not down in the bass where the lhLow/lhHigh anchor range lives —
  // buried rootless voicings turn to mud.
  const l4High = Math.max(lhHigh, 64);
  const l4Target = Math.max(lhTarget, 56);
  const candA = placeOffsets(spec.rootPc, shapeA, lhLow, l4High, l4Target, prevLeft);
  const candB = placeOffsets(spec.rootPc, shapeB, lhLow, l4High, l4Target, prevLeft);
  const scoreOf = (cand) =>
    prevLeft && prevLeft.length
      ? arrayDistance(prevLeft, cand)
      : Math.abs(cand.reduce((a, b) => a + b, 0) / cand.length - l4Target);
  const left = scoreOf(candA) <= scoreOf(candB) ? candA : candB;
  // Right hand is free/optional at this level — nothing prescribed.
  return { left, right: [] };
};

// The chord's defining color tones: the 3rd (quality) and the 7th (or 6th),
// as pitch classes. Null seventh when the chord has neither.
export const guideTones = (spec) => {
  const info = baseById(spec.base);
  const third = info ? normPc(spec.rootPc + info.intervals[1]) : null;
  const sev = spec.seventh ? seventhById(spec.seventh) : null;
  const seventh = sev ? normPc(spec.rootPc + sev.semis) : null;
  return { third, seventh };
};

// Total |Δ| between two {left,right} voicings — each hand's midis sorted
// and paired up to the shorter list's length (extra notes in the longer
// hand aren't counted against anything).
export const voicingDistance = (a, b) => {
  if (!a || !b) return Infinity;
  return arrayDistance(a.left || [], b.left || []) + arrayDistance(a.right || [], b.right || []);
};

// Per-hand movement between two voicings, plus the right hand's note-by-note
// story (sorted midis paired up to the shorter list, same convention as
// voicingDistance): rightPairs = [{ from, to }] where from === to means the
// finger stayed put. The bass walking roots is expected motion; the right
// hand's slither is the number worth teaching — so callers can headline
// `right` and mention `left` quietly.
export const voicingMoves = (prev, next) => {
  if (!prev || !next) return null;
  const left = arrayDistance(prev.left || [], next.left || []);
  const right = arrayDistance(prev.right || [], next.right || []);
  const sa = [...(prev.right || [])].sort((x, y) => x - y);
  const sb = [...(next.right || [])].sort((x, y) => x - y);
  const n = Math.min(sa.length, sb.length);
  const rightPairs = [];
  for (let i = 0; i < n; i += 1) rightPairs.push({ from: sa[i], to: sb[i] });
  return { left, right, rightPairs };
};

/* ---------- Progressions ---------- */
// Steps are offsets (in semitones) from the tonic, so borrowed chords
// (bVII, bVI, ...) fall out naturally regardless of major/minor. `mode` is
// display metadata; the tonic itself is always the selected key's tonicPc
// (a minor-mode progression plays in that key's parallel minor).

export const PROGRESSIONS = [
  {
    id: "axis",
    name: "Axis (I-V-vi-IV)",
    famousIn: ["pop anthems", "arena rock", "worship music"],
    mode: "major",
    beatsPerChord: 4,
    steps: [
      { offset: 0, roman: "I", base: "maj" },
      { offset: 7, roman: "V", base: "maj" },
      { offset: 9, roman: "vi", base: "min" },
      { offset: 5, roman: "IV", base: "maj" },
    ],
  },
  {
    id: "doo-wop",
    name: "Doo-wop (I-vi-IV-V)",
    famousIn: ["50s doo-wop", "ballads", "oldies radio"],
    mode: "major",
    beatsPerChord: 4,
    steps: [
      { offset: 0, roman: "I", base: "maj" },
      { offset: 9, roman: "vi", base: "min" },
      { offset: 5, roman: "IV", base: "maj" },
      { offset: 7, roman: "V", base: "maj" },
    ],
  },
  {
    id: "andalusian",
    name: "Andalusian (i-bVII-bVI-V7)",
    famousIn: ["flamenco", "classical guitar", "dramatic film scores"],
    mode: "minor",
    beatsPerChord: 4,
    steps: [
      { offset: 0, roman: "i", base: "min" },
      { offset: 10, roman: "bVII", base: "maj" },
      { offset: 8, roman: "bVI", base: "maj" },
      { offset: 7, roman: "V7", base: "maj", seventh: "b7" },
    ],
  },
  {
    id: "darkwave",
    name: "Darkwave (i-bVI-III-bVII)",
    famousIn: ["synthwave", "post-punk", "goth club nights"],
    mode: "minor",
    beatsPerChord: 4,
    steps: [
      { offset: 0, roman: "i", base: "min" },
      { offset: 8, roman: "bVI", base: "maj" },
      { offset: 3, roman: "III", base: "maj" },
      { offset: 10, roman: "bVII", base: "maj" },
    ],
  },
  {
    id: "mixolydian-rock",
    name: "Mixolydian rock (I-bVII-IV)",
    famousIn: ["classic rock", "jam bands", "Britpop"],
    mode: "major",
    beatsPerChord: 4,
    steps: [
      { offset: 0, roman: "I", base: "maj" },
      { offset: 10, roman: "bVII", base: "maj" },
      { offset: 5, roman: "IV", base: "maj" },
    ],
  },
  {
    id: "two-five-one",
    name: "Two-five-one (iim7-V7-Imaj7)",
    famousIn: ["jazz standards", "bossa nova", "city pop"],
    mode: "major",
    beatsPerChord: 4,
    steps: [
      { offset: 2, roman: "iim7", base: "min", seventh: "b7" },
      { offset: 7, roman: "V7", base: "maj", seventh: "b7" },
      { offset: 0, roman: "Imaj7", base: "maj", seventh: "maj7" },
    ],
  },
  {
    id: "neo-soul-vamp",
    name: "Neo-soul vamp (im9-ivm9)",
    famousIn: ["neo-soul", "R&B", "gospel keys"],
    mode: "minor",
    beatsPerChord: 4,
    steps: [
      { offset: 0, roman: "im9", base: "min", seventh: "b7", extensions: ["9"] },
      { offset: 5, roman: "ivm9", base: "min", seventh: "b7", extensions: ["9"] },
    ],
  },
  {
    id: "twelve-bar-blues",
    name: "12-bar blues (all dominant 7)",
    famousIn: ["blues", "early rock and roll", "boogie-woogie"],
    mode: "major",
    beatsPerChord: 4,
    steps: [
      { offset: 0, roman: "I7", base: "maj", seventh: "b7" },
      { offset: 0, roman: "I7", base: "maj", seventh: "b7" },
      { offset: 0, roman: "I7", base: "maj", seventh: "b7" },
      { offset: 0, roman: "I7", base: "maj", seventh: "b7" },
      { offset: 5, roman: "IV7", base: "maj", seventh: "b7" },
      { offset: 5, roman: "IV7", base: "maj", seventh: "b7" },
      { offset: 0, roman: "I7", base: "maj", seventh: "b7" },
      { offset: 0, roman: "I7", base: "maj", seventh: "b7" },
      { offset: 7, roman: "V7", base: "maj", seventh: "b7" },
      { offset: 5, roman: "IV7", base: "maj", seventh: "b7" },
      { offset: 0, roman: "I7", base: "maj", seventh: "b7" },
      { offset: 0, roman: "I7", base: "maj", seventh: "b7" },
    ],
  },
  {
    id: "soundtrack-lift",
    name: "Soundtrack lift (i-bVI)",
    famousIn: ["film scores", "trailer music", "post-rock builds"],
    mode: "minor",
    beatsPerChord: 4,
    steps: [
      { offset: 0, roman: "i", base: "min" },
      { offset: 8, roman: "bVI", base: "maj" },
    ],
  },
  {
    id: "royal-road",
    name: "Royal road (IV-V-iii-vi)",
    famousIn: ["J-pop", "anime openings", "city pop"],
    mode: "major",
    beatsPerChord: 4,
    steps: [
      { offset: 5, roman: "IV", base: "maj" },
      { offset: 7, roman: "V", base: "maj" },
      { offset: 4, roman: "iii", base: "min" },
      { offset: 9, roman: "vi", base: "min" },
    ],
  },
  {
    id: "plagal-amen",
    name: "Plagal amen (IV-I)",
    famousIn: ["hymns", "gospel", "choral finales"],
    mode: "major",
    beatsPerChord: 4,
    steps: [
      { offset: 5, roman: "IV", base: "maj" },
      { offset: 0, roman: "I", base: "maj" },
    ],
  },
];

// A step's seventh at 'warm'/'lush': an explicit step.seventh wins;
// otherwise minor and diminished chords take a b7 (m7 / half-diminished),
// a functioning dominant (roman V/V7) takes b7, and any other major chord
// takes maj7 — so every step actually warms up, not just the ones that
// spelled a seventh in their name.
const stepSeventh = (step) => {
  if (step.seventh) return step.seventh;
  if (step.base === "min" || step.base === "dim") return "b7";
  if (step.base === "maj") return /^V7?$/.test(step.roman) ? "b7" : "maj7";
  return null; // sus bases stay open
};

// Expand a progression in a key at a color level: 'plain' (triads only),
// 'warm' (sevenths on every step, per stepSeventh), 'lush' (sevenths plus
// the step's extensions, defaulting to a 9). Returns
// [{ spec, symbol, roman, beats }, ...].
export const progressionChords = (prog, keyName, colorLevel) => {
  const key = keyByName(keyName);
  if (!prog || !key) return [];
  return prog.steps.map((step) => {
    const rootPc = normPc(key.tonicPc + step.offset);
    let seventh = null;
    let extensions = [];
    if (colorLevel === "warm") {
      seventh = stepSeventh(step);
    } else if (colorLevel === "lush") {
      seventh = stepSeventh(step);
      extensions = step.extensions ? [...step.extensions] : ["9"];
    }
    const spec = { rootPc, base: step.base, seventh, extensions };
    // Borrowed flat-side chords (bVI, bVII, bIII...) spell flat even in
    // sharp keys — Abmaj9 in C, never G#maj9.
    const flatSpelling = key.useFlats || step.roman.startsWith("b");
    return {
      spec,
      symbol: chordSymbol(spec, flatSpelling),
      roman: step.roman,
      beats: step.beats || prog.beatsPerChord,
    };
  });
};

/* ---------- Drill check helpers ---------- */

// Pitch-class set equality against the chord's spec — any octave, any
// doubling. Mirrors chordCaller.matchesCard's played-note logic.
export const matchesChord = (playedMidis, spec) => {
  if (!playedMidis || !playedMidis.length) return false;
  const want = chordPcs(spec);
  const played = new Set(playedMidis.map((m) => normPc(m)));
  if (played.size !== want.length) return false;
  return want.every((pc) => played.has(pc));
};

// Exact-midi match (order-free) between what was played and a voicing's
// left+right midis combined; `tolerance` is how many notes may be wrong
// (0 = every midi must match exactly).
export const matchesVoicing = (playedMidis, voicing, tolerance = 0) => {
  if (!voicing) return false;
  const target = [...(voicing.left || []), ...(voicing.right || [])].sort((a, b) => a - b);
  const played = [...(playedMidis || [])].sort((a, b) => a - b);
  if (target.length !== played.length) return false;
  let mismatches = 0;
  for (let i = 0; i < target.length; i += 1) {
    if (target[i] !== played[i]) mismatches += 1;
  }
  return mismatches <= tolerance;
};
